import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  applyCatalogSetup,
  getCatalogSetupState,
  previewCatalogSetup,
  requestCatalogWriteScope,
  type CatalogSetupPreview,
  type CatalogSetupState,
} from './catalogApi';

export function CatalogSetupPanel() {
  const [state, setState] = useState<CatalogSetupState | null>(null);
  const [title, setTitle] = useState('Wholesale catalog');
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<CatalogSetupPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const result = await getCatalogSetupState();
    setState(result);
    setSelectedLocationIds((current) => {
      const available = new Set(
        result.locations
          .filter((item) => item.active_catalog_count === 0)
          .map((item) => item.id),
      );
      if (current.length > 0) {
        return current.filter((id) => available.has(id));
      }
      return [...available];
    });
    return result;
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    refresh()
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : 'Could not load catalog setup.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refresh]);

  const selectedLocations = useMemo(() => {
    const ids = new Set(selectedLocationIds);
    return state?.locations.filter((item) => ids.has(item.id)) ?? [];
  }, [selectedLocationIds, state]);

  const toggleLocation = useCallback((id: string, checked: boolean) => {
    setPreview(null);
    setSelectedLocationIds((current) => {
      if (checked) return current.includes(id) ? current : [...current, id];
      return current.filter((value) => value !== id);
    });
  }, []);

  const previewPlan = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await previewCatalogSetup({
        title: title.trim(),
        company_location_ids: selectedLocationIds,
      });
      setPreview(result);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not preview catalog setup.');
    } finally {
      setBusy(false);
    }
  }, [selectedLocationIds, title]);

  const requestWriteScope = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await requestCatalogWriteScope();
      const refreshed = await refresh();
      if (refreshed.write_products_granted) {
        setNotice('Shopify catalog write permission is now granted for this store.');
      } else {
        setNotice(
          'Permission was requested. Shopify may take a few seconds to deliver the scope update; refresh this section before applying.',
        );
      }
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not request Shopify permission.');
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const applyPlan = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await applyCatalogSetup({
        title: title.trim(),
        company_location_ids: selectedLocationIds,
      });
      setPreview(null);
      setNotice(
        `Created ${result.catalog.title} in Shopify and queued a verification sync. ` +
          'The B2B Health Check can be rerun after synchronization completes.',
      );
      await refresh();
      setSelectedLocationIds([]);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not apply catalog setup.');
    } finally {
      setBusy(false);
    }
  }, [refresh, selectedLocationIds, title]);

  if (loading) {
    return (
      <s-section heading="Catalog setup & assignment">
        <s-paragraph>Loading Shopify B2B catalog context…</s-paragraph>
      </s-section>
    );
  }

  return (
    <s-section heading="Catalog setup & assignment">
      <s-stack direction="block" gap="small">
        <s-paragraph>
          Fix company locations that have no active B2B catalog. Atellius previews the exact change
          before Shopify is modified, then runs a verification sync after the change.
        </s-paragraph>

        {!state?.write_allowed && (
          <s-paragraph>
            Catalog writes are currently blocked by the Atellius remote-validation allowlist for
            this store.
          </s-paragraph>
        )}

        <s-text-field
          label="Catalog name"
          value={title}
          onInput={(event: Event) => {
            setTitle((event.currentTarget as HTMLElement & { value: string }).value);
            setPreview(null);
          }}
        />

        <s-heading>Company locations</s-heading>
        {state?.locations.length ? (
          <div style={{ display: 'grid', gap: '10px' }}>
            {state.locations.map((location) => (
              <label key={location.id} style={{ display: 'flex', gap: '10px', alignItems: 'start' }}>
                <input
                  type="checkbox"
                  checked={selectedLocationIds.includes(location.id)}
                  disabled={location.active_catalog_count > 0}
                  onChange={(event) => toggleLocation(location.id, event.currentTarget.checked)}
                />
                <span>
                  <strong>{location.company_name}</strong> — {location.name}
                  {location.active_catalog_count > 0
                    ? ` · ${location.active_catalog_count} active catalog(s) already assigned`
                    : ' · no active catalog'}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <s-paragraph>No synchronized company locations are available.</s-paragraph>
        )}

        <s-paragraph>
          Selected: {selectedLocations.length} location{selectedLocations.length === 1 ? '' : 's'}.
          This first setup uses Shopify sales-channel product visibility and does not create a price
          list or curated publication.
        </s-paragraph>

        <s-button
          onClick={() => void previewPlan()}
          disabled={busy || !title.trim() || selectedLocationIds.length === 0}
        >
          {busy ? 'Working…' : 'Preview catalog setup'}
        </s-button>

        {preview && (
          <s-stack direction="block" gap="small">
            <s-heading>Change preview</s-heading>
            <s-paragraph>
              Create active Shopify company-location catalog “{preview.title}” and assign it to{' '}
              {preview.company_location_count} selected location
              {preview.company_location_count === 1 ? '' : 's'}.
            </s-paragraph>
            <s-paragraph>
              Product visibility: sales channel · custom price list: none · curated publication:
              none.
            </s-paragraph>

            {!preview.write_products_granted && state?.write_allowed && (
              <s-button onClick={() => void requestWriteScope()} disabled={busy}>
                Grant catalog write permission
              </s-button>
            )}

            {preview.write_products_granted && preview.write_allowed && (
              <s-button variant="primary" onClick={() => void applyPlan()} disabled={busy}>
                Apply to Shopify
              </s-button>
            )}
          </s-stack>
        )}

        {notice && <s-paragraph>{notice}</s-paragraph>}
        {error && <s-paragraph>Catalog setup needs attention: {error}</s-paragraph>}
      </s-stack>
    </s-section>
  );
}
